import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Alert, Button, IconButton, SegmentedTabs } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { useStayPacks } from "@/hooks/useStayPacks";
import NewReservationDialog from "../reservation/NewReservationDialog";
import { CheckInDialog } from "../stay/CheckInDialog";
import { FollowUpDialog } from "../stay/FollowUpDialog";
import type { PanelPet } from "../panel/panelModel";
import MonthGrid from "../schedule/MonthGrid";
import WeekGrid from "../schedule/WeekGrid";
import DayReservationsCard from "../schedule/DayReservationsCard";
import { useStaySchedule } from "../schedule/useStaySchedule";
import { RescheduleStayDayDialog } from "../schedule/RescheduleStayDayDialog";
import { useDaycarePlanDays } from "../plans/useDaycarePlanDays";
import { useStayPlanContracts } from "@/hooks/useStayPlanContracts";
import { useProfile } from "@/hooks/useProfile";
import { useActivePlanBirthdayPets } from "../schedule/useActivePlanBirthdayPets";
import {
  addDays,
  addMonths,
  daysInMonth,
  isBirthdayOnDate,
  parseIsoDate,
  resolveDisplayStatus,
  startOfMonth,
  startOfWeek,
  toIsoDate,
  weekCells,
  type StayShowFilter,
} from "../schedule/scheduleModel";
import type { ScheduleReservation } from "../schedule/scheduleModel";
import { actionForReservationStatus } from "../schedule/scheduleInteraction";

type ScheduleView = "month" | "week";

const VIEW_STORAGE_KEY = "daycare-hotel:schedule-view";

function readStoredView(): ScheduleView {
  try {
    const value = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return value === "week" ? "week" : "month";
  } catch {
    return "month";
  }
}

function storeView(value: ScheduleView) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, value);
  } catch {
    // Ignore unavailable localStorage.
  }
}

export interface ScheduleTabProps {
  show?: StayShowFilter;
}

export default function ScheduleTab({ show = "all" }: ScheduleTabProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";

  const [view, setViewState] = useState<ScheduleView>(readStoredView);
  const setView = (value: ScheduleView) => {
    setViewState(value);
    storeView(value);
  };
  const [selectedDate, setSelectedDate] = useState(() => toIsoDate(new Date()));
  const [reservationOpen, setReservationOpen] = useState(false);
  // Guarda só o id: derivar de `reservations` a cada render mantém o modal em
  // dia com o que a ação acabou de mudar, em vez de um snapshot congelado.
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<{ reservation: ScheduleReservation; dayId: string } | null>(
    null,
  );

  const anchor = useMemo(() => parseIsoDate(selectedDate), [selectedDate]);

  /** The grid drives the fetch window, so month and week each load exactly what they draw. */
  const range = useMemo(() => {
    if (view === "week") {
      const days = weekCells(anchor);
      return { from: days[0], to: days[6] };
    }
    const first = startOfMonth(anchor);
    return {
      from: toIsoDate(first),
      to: toIsoDate(new Date(anchor.getFullYear(), anchor.getMonth(), daysInMonth(anchor))),
    };
  }, [anchor, view]);

  const schedule = useStaySchedule(range);
  const { data: profile } = useProfile();
  // A remarcação precisa saber se aquele pet tem matrícula: é ela que decide
  // entre mover direto e abrir a pergunta de 3 vias.
  const rescheduleContracts = useStayPlanContracts(
    "daycare",
    profile?.current_clinic_id ?? null,
    rescheduleTarget ? [rescheduleTarget.reservation.petId] : [],
  ).data;
  const rescheduleContract =
    rescheduleContracts?.find((contract) => contract.status === "active" && contract.usageRule === "per_week") ?? null;
  const reschedulePlanDays = useDaycarePlanDays(rescheduleContract?.id ?? null).data ?? [];
  const packsQuery = useStayPacks();
  const planBirthdaysQuery = useActivePlanBirthdayPets();
  const reservations = schedule.data ?? [];
  const packs = packsQuery.data ?? [];
  const planBirthdayPets = planBirthdaysQuery.data ?? [];

  const step = (direction: -1 | 1) => {
    if (view === "week") {
      setSelectedDate(toIsoDate(addDays(startOfWeek(anchor), direction * 7)));
      return;
    }
    const nextMonth = addMonths(anchor, direction);
    // Keep the same day-of-month when the target month is long enough for it.
    const day = Math.min(anchor.getDate(), daysInMonth(nextMonth));
    setSelectedDate(toIsoDate(new Date(nextMonth.getFullYear(), nextMonth.getMonth(), day)));
  };

  const { title, titleShort } = useMemo(() => {
    if (view === "week") {
      const days = weekCells(anchor);
      const startDate = parseIsoDate(days[0]);
      const endDate = parseIsoDate(days[6]);
      const format = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
      const start = format.format(startDate);
      const end = format.format(endDate);
      const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
      const shortStart = sameMonth ? new Intl.DateTimeFormat(locale, { day: "numeric" }).format(startDate) : start;
      return {
        title: t("schedule.weekRange", { start, end }),
        titleShort: t("schedule.weekRangeShort", { start: shortStart, end }),
      };
    }
    const label = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(anchor);
    const full = label.charAt(0).toUpperCase() + label.slice(1);
    return { title: full, titleShort: full };
  }, [anchor, locale, t, view]);

  const selectedReservation = selectedReservationId
    ? reservations.find((reservation) => reservation.id === selectedReservationId) ?? null
    : null;

  const selectedPet: PanelPet | null = selectedReservation
    ? {
        reservationId: selectedReservation.id,
        stayDayId: selectedReservation.dayIdByDate?.[selectedDate] ?? "",
        petId: selectedReservation.petId,
        petName: selectedReservation.petName,
        ownerId: selectedReservation.ownerId,
        ownerName: selectedReservation.ownerName,
        nature: selectedReservation.nature,
        status: resolveDisplayStatus(selectedReservation, selectedDate),
        size: "unknown",
        period: null,
        checkInTime: selectedReservation.checkInTime,
        checkOutTime: null,
        ownerPhone: null,
        isOvernight: selectedReservation.isOvernight,
        inAdaptation: false,
        bedLabel: selectedReservation.bedLabel,
        stayRange: null,
        lastStayDate: selectedReservation.checkOutDate ?? selectedDate,
        alerts: [],
        incompatibleWith: [],
        isBirthday: isBirthdayOnDate(selectedReservation.birthDate, selectedDate),
      }
    : null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-0.5 rounded-full border border-border bg-card p-[3px]">
          <IconButton
            variant="ghost"
            size="sm"
            label={t("schedule.previous")}
            onClick={() => step(-1)}
          >
            <ChevronLeft size={16} />
          </IconButton>
          <IconButton
            variant="ghost"
            size="sm"
            label={t("schedule.next")}
            onClick={() => step(1)}
          >
            <ChevronRight size={16} />
          </IconButton>
        </div>

        <h2 className="font-display text-lg font-bold text-foreground">
          <span className="sm:hidden">{titleShort}</span>
          <span className="hidden sm:inline">{title}</span>
        </h2>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <SegmentedTabs
            variant="outline-secondary"
            label={t("schedule.viewLabel")}
            value={view}
            onValueChange={(value) => setView(value as ScheduleView)}
            items={[
              { value: "month", label: t("schedule.viewMonth") },
              { value: "week", label: t("schedule.viewWeek") },
            ]}
          />
        </div>
      </div>

      {schedule.isError && (
        <Alert className="mb-4" variant="danger">
          {t("schedule.loadError")}
        </Alert>
      )}

      {schedule.isPending ? (
        <Skeleton className="mb-[18px] h-[420px] w-full rounded-2xl" />
      ) : view === "month" ? (
        <MonthGrid
          anchor={anchor}
          packs={packs}
          reservations={reservations}
          planBirthdayPets={planBirthdayPets}
          selectedDate={selectedDate}
          show={show}
          onSelectDate={setSelectedDate}
        />
      ) : (
        <WeekGrid
          anchor={anchor}
          packs={packs}
          reservations={reservations}
          planBirthdayPets={planBirthdayPets}
          selectedDate={selectedDate}
          show={show}
          onSelectDate={setSelectedDate}
        />
      )}

      <DayReservationsCard
        isoDate={selectedDate}
        packs={packs}
        reservations={reservations}
        show={show}
        onNewReservation={() => setReservationOpen(true)}
        onReservationAction={(reservation) => setSelectedReservationId(reservation.id)}
        onReschedule={(reservation, dayId) => setRescheduleTarget({ reservation, dayId })}
      />

      {rescheduleTarget ? (
        <RescheduleStayDayDialog
          open
          onOpenChange={(open) => !open && setRescheduleTarget(null)}
          dayId={rescheduleTarget.dayId}
          petName={rescheduleTarget.reservation.petName}
          currentDate={selectedDate}
          contract={
            rescheduleContract
              ? {
                  id: rescheduleContract.id,
                  usageRule: rescheduleContract.usageRule,
                  usageLimit: rescheduleContract.usageLimit,
                  fixedWeekdays: rescheduleContract.fixedWeekdays,
                  firstDueOn: rescheduleContract.firstDueOn ?? rescheduleContract.startsOn,
                  weekInterval: rescheduleContract.weekInterval,
                  startsOn: rescheduleContract.startsOn,
                }
              : null
          }
          planDays={reschedulePlanDays}
          onDone={() => {
            setRescheduleTarget(null);
            void schedule.refetch();
          }}
        />
      ) : null}

      <NewReservationDialog open={reservationOpen} onOpenChange={setReservationOpen} />
      {selectedPet && actionForReservationStatus(selectedPet.status) === "check_in" && (
        <CheckInDialog pet={selectedPet} isoDate={selectedDate} open onOpenChange={(open) => !open && setSelectedReservationId(null)} />
      )}
      {selectedPet && actionForReservationStatus(selectedPet.status) !== "check_in" && (
        <FollowUpDialog pet={selectedPet} isoDate={selectedDate} open onOpenChange={(open) => !open && setSelectedReservationId(null)} />
      )}
    </div>
  );
}
